import type { CardType } from '../types'

interface DecisionRecord {
  cardType: CardType
  isCorrect: boolean
  timeSpentMs: number
  timestamp: number
}

/**
 * StatisticsEngine - 行为统计引擎
 * 负责记录决策行为、分析行为模式
 */
export class StatisticsEngine {
  private decisions: DecisionRecord[] = []
  private sessionStartTime: number = Date.now()

  /**
   * 记录一次决策
   */
  recordDecision(cardType: CardType, isCorrect: boolean, timeSpentMs: number): void {
    this.decisions.push({
      cardType,
      isCorrect,
      timeSpentMs,
      timestamp: Date.now(),
    })
  }

  /**
   * 获取行为模式分析
   */
  getBehaviorPatterns() {
    if (this.decisions.length === 0) {
      return {
        overallAccuracy: 0,
        averageDecisionTimeMs: 0,
        decisionsByType: {},
        accuracyByType: {},
      }
    }

    const totalDecisions = this.decisions.length
    const correctDecisions = this.decisions.filter(d => d.isCorrect).length
    const overallAccuracy = correctDecisions / totalDecisions

    const totalTime = this.decisions.reduce((sum, d) => sum + d.timeSpentMs, 0)
    const averageDecisionTimeMs = Math.round(totalTime / totalDecisions)

    // 按卡片类型分组统计
    const decisionsByType: Record<string, number> = {}
    const correctByType: Record<string, number> = {}

    for (const decision of this.decisions) {
      decisionsByType[decision.cardType] = (decisionsByType[decision.cardType] ?? 0) + 1
      if (decision.isCorrect) {
        correctByType[decision.cardType] = (correctByType[decision.cardType] ?? 0) + 1
      }
    }

    // 计算每种类型的正确率
    const accuracyByType: Record<string, number> = {}
    for (const [type, count] of Object.entries(decisionsByType)) {
      const correct = correctByType[type] ?? 0
      accuracyByType[type] = correct / count
    }

    return {
      overallAccuracy,
      averageDecisionTimeMs,
      decisionsByType,
      accuracyByType,
    }
  }

  /**
   * 获取总学习时间
   */
  getStudyTimeMs(): number {
    return Date.now() - this.sessionStartTime
  }

  /**
   * 重置会话
   */
  resetSession(): void {
    this.decisions = []
    this.sessionStartTime = Date.now()
  }

  /**
   * 获取原始决策记录（用于调试/导出）
   */
  getRawDecisions(): DecisionRecord[] {
    return [...this.decisions]
  }
}
